import React, { useMemo } from "react";
import CountUp from "react-countup";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";

/**
 * How the experts are actually performing.
 *
 * The platform wanted a record of how many requesters each expert saw through
 * and how satisfied those requesters were, so it could recognise the ones doing
 * the work. Until now nothing of the sort existed anywhere — not a column, not
 * a field. The figures come from the expert payload itself, so they cost no
 * extra request and cannot disagree with the table below them.
 *
 * `satisfaction_rate` is null for an expert nobody has judged yet. That is not
 * the same as 0 %, and the two must never be shown alike: a new expert would
 * otherwise look like the worst on the platform.
 */

/** Tone follows the rate, so a struggling expert is visible at a glance. */
function rateTone(rate) {
  if (rate === null || rate === undefined) return "secondary";
  if (rate >= 80) return "success";
  if (rate >= 50) return "warning";
  return "danger";
}

function StatCard({ label, hint, value, icon, tone, suffix }) {
  return (
    <Col xl={3} md={6}>
      <Card className="card-animate">
        <CardBody>
          <div className="d-flex align-items-center">
            <div className="flex-grow-1 overflow-hidden">
              <p className="text-uppercase fw-medium text-muted mb-0 text-truncate">
                {label}
              </p>
              {hint ? <p className="text-muted fs-11 mb-0">{hint}</p> : null}
              <h2 className="mt-4 ff-secondary fw-semibold">
                <CountUp
                  start={0}
                  end={value}
                  duration={1}
                  separator=" "
                  suffix={suffix || ""}
                />
              </h2>
            </div>
            <div className="flex-shrink-0 ms-2">
              <div
                className={`avatar-sm rounded-circle bg-${tone}-subtle d-flex align-items-center justify-content-center`}
              >
                <i className={`${icon} fs-2 text-${tone}`} />
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
}

function ExpertsPerformance({ experts = [], t }) {
  const stats = useMemo(() => {
    const validated = experts.filter((e) => e.is_validated);
    const handled = experts.reduce(
      (sum, e) => sum + (e.questions_handled || 0),
      0
    );
    const satisfied = experts.reduce(
      (sum, e) => sum + (e.questions_satisfied || 0),
      0
    );

    return {
      totalExperts: experts.length,
      validatedExperts: validated.length,
      handled,
      // Platform-wide rate: satisfied verdicts over all judged exchanges, not
      // an average of per-expert rates — that would give an expert with two
      // requesters the same weight as one with two hundred.
      satisfactionRate: handled > 0 ? Math.round((satisfied / handled) * 100) : 0,
    };
  }, [experts]);

  // Only experts who have actually been judged can be ranked.
  const ranking = useMemo(
    () =>
      experts
        .filter((e) => (e.questions_handled || 0) > 0)
        .sort((a, b) => {
          const rate = (b.satisfaction_rate || 0) - (a.satisfaction_rate || 0);
          // Ties go to whoever handled more people.
          return rate !== 0
            ? rate
            : (b.questions_handled || 0) - (a.questions_handled || 0);
        })
        .slice(0, 5),
    [experts]
  );

  const unrated = useMemo(
    () => experts.filter((e) => e.is_validated && !(e.questions_handled > 0)).length,
    [experts]
  );

  if (experts.length === 0) return null;

  return (
    <>
      <Row>
        <StatCard
          label={t("Experts")}
          hint={t("Dont {{count}} validés", { count: stats.validatedExperts })}
          value={stats.totalExperts}
          icon="ri-shield-user-line"
          tone="primary"
        />
        <StatCard
          label={t("Demandeurs accompagnés")}
          hint={t("Échanges clôturés")}
          value={stats.handled}
          icon="ri-team-line"
          tone="info"
        />
        <StatCard
          label={t("Taux de satisfaction")}
          hint={t("Sur l'ensemble des échanges jugés")}
          value={stats.satisfactionRate}
          suffix=" %"
          icon="ri-emotion-happy-line"
          tone={rateTone(stats.satisfactionRate)}
        />
        <StatCard
          label={t("Experts sans évaluation")}
          hint={t("Validés, jamais sollicités")}
          value={unrated}
          icon="ri-user-search-line"
          tone="warning"
        />
      </Row>

      {ranking.length > 0 ? (
        <Row>
          <Col xs={12}>
            <Card>
              <CardHeader className="border-0 align-items-center d-flex">
                <h4 className="card-title mb-0 flex-grow-1">
                  {t("Experts les mieux évalués")}
                </h4>
                <span className="text-muted fs-12">
                  {t("Top {{shown}} sur {{total}}", {
                    shown: ranking.length,
                    total: stats.totalExperts,
                  })}
                </span>
              </CardHeader>
              <CardBody className="pt-0">
                <p className="text-muted fs-12 mb-3">
                  {t(
                    "Classement par taux de satisfaction, puis par nombre de demandeurs accompagnés."
                  )}
                </p>
                <ul className="list-unstyled vstack gap-3 mb-0">
                  {ranking.map((expert) => {
                    const rate = expert.satisfaction_rate ?? 0;
                    const tone = rateTone(expert.satisfaction_rate);

                    return (
                      <li key={expert.id}>
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <span className="fw-medium text-truncate me-2">
                            <Link
                              to={`/experts-details/${expert.id}`}
                              className="text-body"
                            >
                              {expert.user?.username || "-"}
                            </Link>
                            {expert.is_certified ? (
                              <span className="badge bg-success-subtle text-success ms-2">
                                <i className="ri-verified-badge-fill me-1" />
                                {t("Certifié")}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-muted fs-12 flex-shrink-0">
                            {t("{{count}} demandeur(s)", {
                              count: expert.questions_handled || 0,
                            })}
                            {" · "}
                            <span className={`text-${tone} fw-semibold`}>
                              {rate} %
                            </span>
                          </span>
                        </div>
                        <div className="progress" style={{ height: 6 }}>
                          <div
                            className={`progress-bar bg-${tone}`}
                            role="progressbar"
                            style={{ width: `${rate}%` }}
                            aria-valuenow={rate}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : null}
    </>
  );
}

export default ExpertsPerformance;
